import yaml from "js-yaml";
import { calculateTotalDuration, formatDuration, toSeconds } from "./time";
import {
  isInjectedChangeover,
  isProtocolWindowGroup,
  formatLoadSummary,
} from "./planVisuals";
import { buildPhaseStatsMap, correctedRepsById, formatPhaseSummary } from "./phaseLog";
import { rpeLabel } from "../config/rpe";
import { copyTextToClipboard } from "./shareLinks";

const HEADER = "# KHRONOS AI Session";

/**
 * Recursively strip internal-only fields (id, autoStart) and parser-injected
 * changeover nodes (transitions AND Setup sections) from plan nodes so the
 * exported YAML is the clean authored session. Changeovers are padding inserted
 * by the compiler/injector, not part of a shared/saved session (issue #22).
 */
const cleanNode = (node) => {
  if (!node) return node;
  const { id, autoStart, ...rest } = node;
  if (rest.children && Array.isArray(rest.children)) {
    rest.children = rest.children
      .filter((child) => !isInjectedChangeover(child))
      .map(cleanNode);
  }
  return rest;
};

/**
 * Serialize a workout plan into a human-readable YAML string.
 */
export const serializeSession = (plan) => {
  if (!plan) return "";

  const totalSeconds = calculateTotalDuration(plan);
  const duration = formatDuration(totalSeconds) || "unknown";

  const doc = {
    name: plan.label || "Untitled session",
    ...(plan.description && { description: plan.description }),
    duration_estimate: duration,
    type: plan.type || "Workout",
    exercises: (plan.children || [])
      .filter((child) => !isInjectedChangeover(child))
      .map(cleanNode),
  };

  const yamlStr = yaml.dump(doc, {
    indent: 2,
    lineWidth: 120,
    noRefs: true,
    quotingType: '"',
    forceQuotes: false,
  });

  return `${HEADER}\n${yamlStr}`;
};

/**
 * Deserialize a YAML string back into a workout plan object.
 * Returns null if the text is not valid YAML or missing required structure.
 */
export const deserializeSession = (text) => {
  if (!text || typeof text !== "string") return null;

  try {
    // Strip the header comment line if present
    const cleaned = text.replace(/^#.*\n?/gm, "").trim();
    if (!cleaned) return null;

    const doc = yaml.load(cleaned);
    if (!doc || typeof doc !== "object") return null;

    // Must have exercises array
    if (!Array.isArray(doc.exercises) || doc.exercises.length === 0) return null;

    // Reconstruct the plan tree
    const plan = {
      label: doc.name || "Imported session",
      description: doc.description || "",
      type: doc.type || "Workout",
      children: doc.exercises,
    };

    // Basic validation: every node needs at least a label or type
    const isValid = (node) => {
      if (!node || typeof node !== "object") return false;
      if (!node.label && !node.type) return false;
      if (node.children && Array.isArray(node.children)) {
        return node.children.every(isValid);
      }
      return true;
    };

    if (!isValid(plan)) return null;

    return plan;
  } catch {
    return null;
  }
};

// --- Summary value column ---

const fmtTime = (sec) => {
  // Round first: actual per-phase times come from averaged logged durations,
  // which are fractional and would otherwise print a long decimal tail. (#39)
  const total = Math.max(0, Math.round(sec) || 0);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2)}:${String(s).padStart(2, "0")}`;
};

// One figure in the value column, unpadded — the caller aligns the column.
const fmtScalar = (value, unit) => {
  if (unit === "reps") return `${value}x`;
  if (unit === "cal") return `${value}cal`;
  if (unit === "m") return fmtTime(value * 60).trimStart();
  if (unit === "s") return value >= 60 ? fmtTime(value).trimStart() : `${value}s`;
  return String(value);
};

// A per-set ramp as one token — "10/12/14x", "100/80/60cal". The unit is stated
// once at the end rather than after every figure, which is what makes the ramp
// readable at a glance. Durations mix badly under a single suffix (a 90s set
// wants M:SS, a 45s one does not), so they take clock form for the whole ramp
// as soon as any leg reaches a minute.
const fmtRamp = (values, unit) => {
  if (unit === "s" || unit === "m") {
    const secs = values.map((v) => (unit === "m" ? v * 60 : v));
    if (secs.some((s) => s >= 60)) {
      return secs.map((s) => fmtTime(s).trimStart()).join("/");
    }
    return `${secs.join("/")}s`;
  }
  if (unit === "reps") return `${values.join("/")}x`;
  if (unit === "cal") return `${values.join("/")}cal`;
  return values.join("/");
};

// The value one leaf prescribes. A `setScheme` ramp wins over the static field,
// which by schema rule only mirrors set 1 — printing that alone turned a
// 10/12/14 circuit into "10 reps every round" (the plan view has read the ramp
// since issue #42; the copied summary had not). A distance target is stored as
// estimated seconds plus provenance, so the metres are the real prescription
// (issue #63) and still come first.
const fmtVal = (node) => {
  const metres = Number(node?.estimatedFromDistanceM);
  if (Number.isFinite(metres) && metres > 0) return `${Math.round(metres)}m`;
  const ramp = node?.setScheme?.value;
  if (Array.isArray(ramp)) {
    const legs = ramp.filter((v) => Number.isFinite(v));
    if (legs.length > 0) return fmtRamp(legs, node.unit);
  }
  if (!node?.value) return "";
  return fmtScalar(node.value, node.unit);
};

/**
 * Format a session plan + phase_log into a human-readable monospace summary.
 *
 * `meta` carries the session-level feedback captured on completion — the
 * perceived-effort rating and free-text notes — which live on the session
 * record rather than the plan, so callers pass them in (issue #43). It also
 * carries the recorded `durationMinutes`, which wins over the plan's estimate:
 * a session that happened took as long as it took, and a logged one's tree has
 * no rests in it, so its estimate can be a fraction of the time the user gave.
 */
export const formatSessionSummary = (planData, meta = {}) => {
  if (!planData) return "";

  const phaseLog = planData.phase_log || [];
  // Per-phase stats rather than a bare total: a phase run once still reports
  // its single split, but a looped one reports the average and spread, which is
  // what you compare between sessions. Same map the history detail renders
  // from, so the copied text and the on-screen breakdown cannot disagree.
  const statsMap = buildPhaseStatsMap(phaseLog);
  // Reps done per set, where they differ from the prescription (#144).
  const repsDone = correctedRepsById(phaseLog);

  // Load column, pre-computed for the whole tree so the width is known before
  // the first row is written — and so a session that prescribes no load at all
  // (bodyweight circuits) never pays for an empty column.
  //
  // Keyed by node REFERENCE, not id: a plan pasted back in through
  // deserializeSession has had every id stripped, and keying those leaves by
  // `undefined` would print the last exercise's weight against all of them.
  const loadMap = new Map();
  let loadWidth = 0;
  // Values are measured the same way. They used to be a fixed five characters,
  // which only held while every figure was a single scalar; a ramp is as wide
  // as the session makes it, so the column is sized from the widest one — with
  // five as the floor so a scalar-only session prints exactly as it always has.
  const valMap = new Map();
  let valWidth = 5;
  const collectColumns = (node) => {
    if (!node || isInjectedChangeover(node)) return;
    if (Array.isArray(node.children) && node.children.length > 0) {
      node.children.forEach(collectColumns);
      return;
    }
    const val = fmtVal(node);
    if (val) {
      valMap.set(node, val);
      valWidth = Math.max(valWidth, val.length);
    }
    const text = formatLoadSummary(node);
    if (!text) return;
    loadMap.set(node, text);
    loadWidth = Math.max(loadWidth, text.length);
  };
  (planData.children || []).forEach(collectColumns);

  const recordedMinutes = Number(meta.durationMinutes);
  const totalSec =
    Number.isFinite(recordedMinutes) && recordedMinutes > 0
      ? Math.round(recordedMinutes) * 60
      : calculateTotalDuration(planData);
  const lines = [];
  lines.push(`Session: ${planData.label || "Untitled"}`);
  lines.push(`Duration: ${formatDuration(totalSec) || "unknown"}`);

  const rating = Number(meta.rating);
  if (Number.isFinite(rating) && rating >= 1 && rating <= 10) {
    const label = rpeLabel(rating);
    lines.push(`Effort: ${Math.round(rating)}/10${label ? ` (${label})` : ""}`);
  }

  lines.push("");

  // Right-aligned into the measured column so the units stack down the page.
  const valCol = (node) => (valMap.get(node) || "").padStart(valWidth);

  const fmtActual = (nodeId) => {
    const stats = nodeId ? statsMap[nodeId] : null;
    const summary = stats ? formatPhaseSummary(stats) : "";
    const reps = nodeId ? repsDone[nodeId] : null;
    return [summary && `[${summary}]`, reps && `done ${reps.join(" · ")}`]
      .filter(Boolean)
      .join(" ");
  };

  // The load column for one row, right-aligned so the units stack. Empty when
  // nothing in the session carries a load.
  const fmtLoad = (node) => {
    if (loadWidth === 0) return "";
    return `${(loadMap.get(node) || "").padStart(loadWidth)}  `;
  };

  // Group heading line: a time-capped container (AMRAP / "for time" cap,
  // issue #36) has a DURATION value, not a round count — print M:SS, not
  // "Nx" (a 15-minute cap printed as "15x:" read as fifteen rounds, #63).
  const groupHeading = (node, pad) => {
    const unit = String(node?.unit || "").toLowerCase();
    if (node?.type === "Group" && (unit === "s" || unit === "m")) {
      return `${pad}${fmtTime(toSeconds(node.value, node.unit))}:`;
    }
    const rounds = node?.type === "Group" && node.value > 0 ? node.value : 1;
    return `${pad}${rounds}x:`;
  };

  // Rest phases sit inline with the exercises around them — no separating
  // blank line, which fragmented the block when pasted elsewhere (issue #43).
  const processChildren = (children, indent) => {
    const pad = "  ".repeat(indent);
    for (const child of children) {
      if (!child) continue;
      if (isInjectedChangeover(child)) continue; // injected padding (issue #22)

      if (child.children && child.children.length > 0) {
        // Group node. An EMOM window's own label ("E2MOM Window") only repeats
        // the format token the enclosing block already states, so the duration
        // line alone carries it (issue #63).
        if (child.label && !isProtocolWindowGroup(child)) {
          lines.push(`${pad}${child.label}:`);
        }
        lines.push(groupHeading(child, pad));
        processChildren(child.children, indent + 1);
      } else {
        // Leaf exercise
        const val = valCol(child);
        const label = (child.label || "").padEnd(28);
        const actual = fmtActual(child.id);
        lines.push(`${pad}  - ${val}  ${label} ${fmtLoad(child)}${actual}`.trimEnd());
      }
    }
  };

  for (const section of planData.children || []) {
    if (!section) continue;
    if (isInjectedChangeover(section)) continue; // injected padding (issue #22)
    if (section.children && section.children.length > 0) {
      if (section.label) lines.push(`${section.label}:`);
      lines.push(groupHeading(section, ""));
      processChildren(section.children, 1);
    } else {
      // Top-level leaf (rare)
      const val = valCol(section);
      const label = (section.label || "").padEnd(28);
      const actual = fmtActual(section.id);
      lines.push(`  - ${val}  ${label} ${fmtLoad(section)}${actual}`.trimEnd());
    }
    lines.push("");
  }

  const notes = typeof meta.notes === "string" ? meta.notes.trim() : "";
  if (notes) {
    lines.push("Notes:");
    for (const line of notes.split("\n")) lines.push(`  ${line}`.trimEnd());
  }

  return lines.join("\n").trimEnd();
};

/**
 * The plan with the user's own recorded splits removed, for sharing a session
 * as what was prescribed rather than how it went (issue #82). Every recorded
 * time the summary prints comes from `phase_log`, so dropping it is enough;
 * the prescription itself is untouched. Corrected reps (#144) go with it:
 * they are how it went, too.
 */
export const withoutRecordedTimes = (planData) => {
  if (!planData) return planData;
  const { phase_log: _phaseLog, ...rest } = planData;
  return rest;
};

/**
 * Copy the human-readable summary to clipboard. Returns true on success.
 */
export const copySummaryToClipboard = async (planData, meta = {}) =>
  copyTextToClipboard(formatSessionSummary(planData, meta));

/**
 * Copy a session plan to the clipboard as YAML text.
 * Returns true on success.
 */
export const copySessionToClipboard = async (plan) =>
  copyTextToClipboard(serializeSession(plan));
